import { Grid, MenuItem, menuItemClasses, SelectChangeEvent } from '@mui/material';
import { useFormikContext } from 'formik';
import { memo, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';

import DownIcon from '../../../../../../../assets/icons/Icon_ArrowDown.svg?react';
import { IcaoIataCode, ResourceType, useQueryGetGetResourcesByType } from '../../../../../../../backend';
import { errorToastConfig } from '../../../../../../../utils/utils';
import { ResourcesPageFormValues } from '../../../../../../pages/ResourcesPage';
import { StyledMuiTextField, StyledSelect } from '../ResourceFieldsLineProperties.style';
import { DcsSelection } from './DcsSelection/DcsSelection';
import { isCheckInProperties } from './ResourceFieldsLine.utils';
import { useReplaceNumberAt, useReplaceSelectAt, useReplaceValueAt } from './useReplaceValueAt';

export const ResourceFieldsLineCheckInProperties = memo(function ResourceFieldsLineCheckInProperties({
    index,
    arrayReplace,
}: {
    index: number;
    arrayReplace: (index: number, value: any) => void;
}) {
    const { values, initialValues, errors } = useFormikContext<ResourcesPageFormValues>();

    const { data: terminals } = useQueryGetGetResourcesByType(
        {
            type: ResourceType.TERMINAL,
            includeNonActive: true,
        },
        {
            onError: () => {
                toast(<FormattedMessage id={'resourcesForm.error.default'} />, errorToastConfig);
            },
        },
    );

    const handleChange = useReplaceValueAt(index, arrayReplace);

    const handleNumberChange = useReplaceNumberAt(index, arrayReplace);

    const handleSelectChange = useReplaceSelectAt(index, arrayReplace);

    const handleDcsChange = useCallback(
        (newValue: IcaoIataCode[]) => {
            arrayReplace(index, {
                ...values.resources[index],
                content: {
                    ...values.resources[index].content,
                    dcs: newValue,
                },
            });
        },
        [arrayReplace, index, values.resources],
    );

    const content = useMemo(() => values.resources[index].content, [index, values.resources]);
    const initialContent = useMemo(
        () => (values.resources[index].id ? initialValues.resources[index].content : null),
        [index, initialValues.resources, values.resources],
    );

    return isCheckInProperties(content) ? (
        <>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    type={'number'}
                    value={content.paxCapacity}
                    onChange={e => handleNumberChange('paxCapacity', e)}
                    modified={
                        values.resources[index].id && isCheckInProperties(initialContent)
                            ? content.paxCapacity !== initialContent.paxCapacity
                            : undefined
                    }
                    InputProps={{
                        inputProps: { min: 1 },
                    }}
                    error={!!errors[`resources[${index}]['paxCapacity']`]}
                    helperText={errors[`resources[${index}]['paxCapacity']`]}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledSelect
                    value={terminals.length === 0 ? '' : content.terminal}
                    onChange={(e: SelectChangeEvent<string>) => handleSelectChange('terminal', e)}
                    IconComponent={DownIcon}
                    modified={
                        values.resources[index].id && isCheckInProperties(initialContent)
                            ? content.terminal !== initialContent.terminal
                            : undefined
                    }
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                [`& .${menuItemClasses.root}`]: {
                                    color: theme => theme.palette.primary.contrastText,
                                },
                            },
                        },
                    }}
                >
                    <MenuItem key={'empty'} value={undefined}>
                        {' '}
                    </MenuItem>
                    {terminals &&
                        terminals
                            .map(r => r.name)
                            .map(name => (
                                <MenuItem key={name} value={name}>
                                    {name}
                                </MenuItem>
                            ))}
                </StyledSelect>
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    type={'string'}
                    value={content.zone}
                    modified={
                        values.resources[index].id && isCheckInProperties(initialContent)
                            ? content.zone !== initialContent.zone
                            : undefined
                    }
                    onChange={e => handleChange('zone', e)}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    type={'string'}
                    value={content.srb}
                    modified={
                        values.resources[index].id && isCheckInProperties(initialContent)
                            ? content.srb !== initialContent.srb
                            : undefined
                    }
                    onChange={e => handleChange('srb', e)}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <DcsSelection
                    dcs={content.dcs}
                    modified={
                        values.resources[index].id && isCheckInProperties(initialContent)
                            ? content.dcs !== initialContent.dcs
                            : undefined
                    }
                    handleDcsChange={handleDcsChange}
                />
            </Grid>
        </>
    ) : null;
});
